import { dateTimeToView, humanizedTimeRange } from "helpers/date";
import { BooleanColumnFilter, DateColumnFilter, NumericColumnFilter, reportFilterWrapper } from "helpers/reactTable";
import { formatNumber } from "helpers/system";
import { matchSorter } from "match-sorter";
import moment from "moment";
import { FilterValue, IdType, Row } from "react-table";

export const REPORT_TYPES = {
    OverallReport: "OverallReport",
    TrailersReport: "TrailersReport",
    MonthlyOverallReport: "MonthlyOverallReport",
    PlanFactReport: "PlanFactReport",
    DriversReport: "DriversReport",
    CitiesReport: "CitiesReport",
    LoadUnloadTimeReport: "LoadUnloadTimeReport",
    ExcessMileageReport: "ExcessMileageReport",
    EmptyMileageBidsPlanFactReport: "EmptyMileageBidsPlanFactReport",
    OverallReportWithVat: "OverallReportWithVat",
    CarsWithoutBidsReport: "CarsWithoutBidsReport",
    CarIdlesReport: "CarIdlesReport",
    BidPointDeviationReport: "BidPointDeviationReport",
    ResponsibleLogistsReport: "ResponsibleLogistsReport",
    DriverAppUsageReport: "DriverAppUsageReport",
    AverageTonnageReport: "AverageTonnageReport",
    BidsCreatedOnTimeReport: "BidsCreatedOnTimeReport",
    BidsWithRegionalFramesReport: "BidsWithRegionalFramesReport",
    BidPriceChangeReport: "BidPriceChangeReport",
    CounterpartiesReport: "CounterpartiesReport",
    DriverAppInstallReport: "DriverAppInstallReport",
    InterBidIntervalReport: "InterBidIntervalReport",
    DailyVehicleMileageReport: "DailyVehicleMileageReport",
    TripScenariosReport: "TripScenariosReport",
    MarginalityReport: "MarginalityReport",
    OrganizationActivityReport: "OrganizationActivityReport",
    RefuelingsReport: "RefuelingsReport",
    DriverWorkAndRestReport: "DriverWorkAndRestReport",
    DriversStatsOfYearReport: "DriversStatsOfYearReport",
    FuelLimitsReport: "FuelLimitsReport",
    DocumentAssignmentProgressReport: "DocumentAssignmentProgressReport",
    FuelCardsPaymentReport: "FuelCardsPaymentReport",
    PlanPointIntersectionsReport: "PlanPointIntersectionsReport",
    PlanFactRefuelingsReport: "PlanFactRefuelingsReport",
    RefuelingsVisitStatsReport: "RefuelingsVisitStatsReport",
    SuitableOrdersReport: "SuitableOrdersReport",
    EmptyMileageReport: "EmptyMileageReport",
    ClientStatisticsReport: "ClientStatisticsReport",
    PlanFactOverallReport: "PlanFactOverallReport",
    LogisticSalaryReport: "LogisticSalaryReport",
    AccountsReceivableReport: "AccountsReceivableReport",
    AccountsPayableReport: "AccountsPayableReport",
    IncomesAndExpensesReport: "IncomesAndExpensesReport",
};

export const REPORT_TYPES_WITH_ODOMETER_MILEAGES = [
    REPORT_TYPES.OverallReport,
    REPORT_TYPES.TrailersReport,
    REPORT_TYPES.MonthlyOverallReport,
    REPORT_TYPES.PlanFactReport,
    REPORT_TYPES.DriversReport,
    REPORT_TYPES.CitiesReport,
    REPORT_TYPES.ExcessMileageReport,
    REPORT_TYPES.ResponsibleLogistsReport,
    REPORT_TYPES.CounterpartiesReport,
    REPORT_TYPES.InterBidIntervalReport,
    REPORT_TYPES.DailyVehicleMileageReport,
    REPORT_TYPES.EmptyMileageReport,
    REPORT_TYPES.LogisticSalaryReport,
];

type Keys = keyof typeof REPORT_TYPES;
export type ReportTypeValues = (typeof REPORT_TYPES)[Keys];

type ReportColumn = {
    clrType: string;
    propertyName: string;
};

export const OVERALL_REPORT_VAT_TOGGLE_COLUMNS = {
    all: ["OverallBidsPrice", "Output", "OverallBidsPriceWithoutVat", "OutputWithoutVat"],
    withNds: ["OverallBidsPrice", "Output"],
    withoutNds: ["OverallBidsPriceWithoutVat", "OutputWithoutVat"],
};

export const GROUP_BY_CURRENT_LOGIST_STORAGE_KEY = "groupByCurrentLogist";

export const getStoredGroupByCurrentLogist = (): boolean => {
    try {
        const item = window.localStorage.getItem(GROUP_BY_CURRENT_LOGIST_STORAGE_KEY);
        return item !== null ? JSON.parse(item) : true;
    } catch {
        return true;
    }
};

export const setStoredGroupByCurrentLogist = (value: boolean): void => {
    try {
        window.localStorage.setItem(GROUP_BY_CURRENT_LOGIST_STORAGE_KEY, JSON.stringify(value));
    } catch (e) {
        console.error(e);
    }
};

export function columnName(column: { propertyName: string }) {
    return column.propertyName.charAt(0).toLowerCase() + column.propertyName.slice(1);
}

export function formatValue(
    value: string | number | string[] | undefined,
    clrType: "percent" | "int64" | "int32" | "double" | "timespan" | "datetimeoffset" | "boolean" | "string[]",
) {
    switch (clrType) {
        case "percent":
            return formatNumber((value || 0) as number, {
                style: "percent",
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
            });
        case "int64":
        case "int32":
            return formatNumber((value || 0) as number);
        case "double":
            return formatNumber((value || 0) as number, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
        case "timespan":
            return value ? humanizedTimeRange(value as string) : "";
        case "datetimeoffset":
            return value ? dateTimeToView(value as string) : "";
        case "boolean":
            return value ? "Да" : "Нет";
        case "string[]":
            if (value && !Array.isArray(value)) {
                value = [value as string];
            }
            return ((value || []) as string[]).join(", ");
        default:
            return value;
    }
}

export function typeSpecificProps(column: ReportColumn) {
    const defaultFilter = {
        filterable: true,
        filter: (rows: Row<{}>[], _id: string, filterValue: FilterValue) => {
            if (!filterValue || !filterValue.value) {
                return rows;
            }
            return rows.filter(
                (row) =>
                    matchSorter([row.values[filterValue.id]], filterValue.value, {
                        threshold: matchSorter.rankings.CONTAINS,
                    }).length !== 0,
            );
        },
    };

    if (["BidId", "PreviousBidId", "CurrentBidId"].includes(column.propertyName)) {
        return defaultFilter;
    }

    switch (column.clrType) {
        case "double":
        case "int64":
        case "int32":
            return {
                filterable: true,
                filter: (rows: Row<{}>[], _id: string, filterValue: FilterValue) => {
                    if (!filterValue || !filterValue.value?.value) {
                        return rows;
                    }
                    return rows.filter((row) => {
                        const numberValue = Number(
                            (filterValue.value.value || "").replace(/[^0-9.,]/g, "").replace(",", "."),
                        );
                        switch (filterValue.value.key) {
                            case "eq":
                                return row.values[filterValue.id] === numberValue;
                            case "gt":
                                return row.values[filterValue.id] > numberValue;
                            case "lt":
                                return row.values[filterValue.id] < numberValue;
                            case "ge":
                                return row.values[filterValue.id] >= numberValue;
                            case "le":
                                return row.values[filterValue.id] <= numberValue;
                            default:
                                return true;
                        }
                    });
                },
                Filter: reportFilterWrapper(NumericColumnFilter),
            };
        case "boolean":
            return {
                filterable: true,
                filterType: "boolean",
                filter: (rows: Row<{}>[], _id: string, filterValue: FilterValue) => {
                    if (!filterValue || !filterValue.value || filterValue.value === "all") {
                        return rows;
                    }
                    return rows.filter((row) =>
                        filterValue.value === "true" ? row.values[filterValue.id] : !row.values[filterValue.id],
                    );
                },
                Filter: reportFilterWrapper((props) =>
                    BooleanColumnFilter({
                        ...props,
                        options: [
                            { label: "Показать все", value: "all" },
                            { label: "Да", value: "true" },
                            { label: "Нет", value: "false" },
                        ],
                    }),
                ),
            };
        case "datetimeoffset":
            return {
                filter: (rows: Row<{}>[], _id: string, filterValue: FilterValue) => {
                    if (!filterValue || !filterValue?.value?.value) {
                        return rows;
                    }
                    const value = moment(filterValue?.value.value);
                    return rows.filter((row) => {
                        switch (filterValue.value.key) {
                            case "eq":
                                return (
                                    dateTimeToView(row.values[filterValue.id]) ===
                                    dateTimeToView(filterValue.value.value)
                                );
                            case "ge":
                                return moment(row.values[filterValue.id]) >= value;
                            case "le":
                                return moment(row.values[filterValue.id]) <= value;
                            default:
                                return true;
                        }
                    });
                },
                Filter: reportFilterWrapper(DateColumnFilter),
                sortType: (rowA: Row<{}>, rowB: Row<{}>, columnId: IdType<{}>) => {
                    if (!rowA.values[columnId] && !rowB.values[columnId]) {
                        return 0;
                    } else if (!rowA.values[columnId]) {
                        return 1;
                    } else if (!rowB.values[columnId]) {
                        return -1;
                    } else {
                        const valueA = moment(rowA.values[columnId]);
                        const valueB = moment(rowB.values[columnId]);
                        if (valueA > valueB) {
                            return -1;
                        }
                        if (valueA < valueB) {
                            return 1;
                        }
                        return 0;
                    }
                },
            };
        case "dateonly":
            return {
                filter: (rows: Row<{}>[], _id: string, filterValue: FilterValue) => {
                    if (!filterValue || !filterValue?.value?.value) {
                        return rows;
                    }
                    const value = moment(filterValue?.value.value).startOf("day");
                    return rows.filter((row) => {
                        const rowValue = moment(row.values[filterValue.id], "YYYY-MM-DD").startOf("day");

                        switch (filterValue.value.key) {
                            case "eq":
                                return rowValue.isSame(value, "day");
                            case "ge":
                                return rowValue >= value;
                            case "le":
                                return rowValue <= value;
                            default:
                                return true;
                        }
                    });
                },
                Filter: reportFilterWrapper((props) => DateColumnFilter({ ...props, dateInputType: "date" })),
                sortType: (rowA: Row<{}>, rowB: Row<{}>, columnId: IdType<{}>) => {
                    if (!rowA.values[columnId] && !rowB.values[columnId]) {
                        return 0;
                    } else if (!rowA.values[columnId]) {
                        return 1;
                    } else if (!rowB.values[columnId]) {
                        return -1;
                    } else {
                        const valueA = moment(rowA.values[columnId], "YYYY-MM-DD");
                        const valueB = moment(rowB.values[columnId], "YYYY-MM-DD");
                        if (valueA > valueB) {
                            return -1;
                        }
                        if (valueA < valueB) {
                            return 1;
                        }
                        return 0;
                    }
                },
            };
        case "timespan":
            return {
                filterable: true,
                filter: (rows: Row<{}>[], _id: string, filterValue: FilterValue) => {
                    if (!filterValue || !filterValue.value) {
                        return rows;
                    }
                    return rows.filter(
                        (row) =>
                            matchSorter([humanizedTimeRange(row.values[filterValue.id])], filterValue.value, {
                                threshold: matchSorter.rankings.CONTAINS,
                            }).length !== 0,
                    );
                },
                sortType: (rowA: Row<{}>, rowB: Row<{}>, columnId: IdType<{}>) => {
                    const valueA = moment.duration(rowA.values[columnId]).asSeconds();
                    const valueB = moment.duration(rowB.values[columnId]).asSeconds();
                    if (valueA > valueB) {
                        return -1;
                    }
                    if (valueA < valueB) {
                        return 1;
                    }
                    return 0;
                },
            };
        case "list`1":
        case "icollection`1":
            return {
                filterable: true,
                filter: (rows: Row<{}>[], _id: string, filterValue: FilterValue) => {
                    if (!filterValue || !filterValue.value) {
                        return rows;
                    }
                    return rows.filter((row) => {
                        const value = row.values[filterValue.id];
                        return (
                            Array.isArray(value) && value.some(({ id }) => id.toString().includes(filterValue.value))
                        );
                    });
                },
                disableSortBy: true,
            };
        case "string[]":
            return {
                filterable: true,
                filter: (rows: Row<{}>[], _id: string, filterValue: FilterValue) => {
                    if (!filterValue || !filterValue.value) {
                        return rows;
                    }
                    return rows.filter((row) => {
                        const value = row.values[filterValue.id];
                        return (
                            Array.isArray(value) &&
                            matchSorter(value, filterValue.value, {
                                threshold: matchSorter.rankings.CONTAINS,
                            }).length !== 0
                        );
                    });
                },
                disableSortBy: true,
            };
        default:
            return defaultFilter;
    }
}
